import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useAssets,
  useDeleteProject,
  useDomains,
  useLogs,
  usePatchProject,
  useProject,
  useZones,
} from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Pencil, Trash2 } from 'lucide-react';
import ProjectForm from '@/components/ProjectForm';
import LogList from '@/components/LogList';
import PhotoGrid from '@/components/PhotoGrid';
import { useAppShell } from '@/components/AppShellContext';
import type { ProjectStatus } from '@/lib/types';

const NEXT_STATUS: Record<ProjectStatus, ProjectStatus[]> = {
  planned: ['active', 'blocked', 'abandoned'],
  active: ['blocked', 'completed', 'planned'],
  blocked: ['active', 'abandoned'],
  completed: ['active', 'archived'],
  abandoned: ['planned', 'archived'],
  archived: ['planned'],
};

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const q = useProject(id);
  const patch = usePatchProject(id ?? '');
  const del = useDeleteProject();
  const domains = useDomains();
  const zones = useZones();
  const logs = useLogs({ entityType: 'project', entityId: id, limit: 50 });
  const photos = useAssets({ entityType: 'project', entityId: id, limit: 60 });
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const { openLogForm, openPhotoSheet } = useAppShell();

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.isError) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data) return null;
  const project = q.data;
  const domain = (domains.data ?? []).find((d) => d.id === project.domainId);
  const zone = (zones.data ?? []).find((z) => z.id === project.zoneId);

  async function onDelete() {
    if (!confirm('Delete this project? This cannot be undone.')) return;
    await del.mutateAsync(project.id);
    navigate('/projects');
  }

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          <Link to="/projects" className="hover:text-foreground">
            Projects
          </Link>{' '}
          <span aria-hidden>·</span> {project.priority}
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
          {project.title}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant={project.status === 'completed' ? 'default' : 'outline'}
            className="font-mono text-[10px] tracking-wider"
          >
            {project.status}
          </Badge>
          {domain ? (
            <Link
              to={`/domains/${domain.slug}`}
              className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              {domain.name}
            </Link>
          ) : null}
          {zone ? (
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {zone.name}
            </span>
          ) : null}
          {project.startedAt ? (
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              started {new Date(project.startedAt).toLocaleDateString()}
            </span>
          ) : null}
          {project.completedAt ? (
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              completed {new Date(project.completedAt).toLocaleDateString()}
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4 mr-2" aria-hidden /> Edit
          </Button>
          {NEXT_STATUS[project.status].map((next) => (
            <Button
              key={next}
              variant="ghost"
              size="sm"
              onClick={() => patch.mutate({ status: next })}
              disabled={patch.isPending}
              data-testid={`status-${next}`}
            >
              → {next}
            </Button>
          ))}
          <Button
            variant="secondary"
            onClick={() =>
              openLogForm({
                domainId: project.domainId ?? undefined,
                entityType: 'project',
                entityId: project.id,
                title: `${project.title} — `,
              })
            }
          >
            Log work
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              openPhotoSheet({
                domainId: project.domainId,
                zoneId: project.zoneId,
                entityType: 'project',
                entityId: project.id,
                tags: ['project'],
              })
            }
          >
            Add photo
          </Button>
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4 mr-2" aria-hidden /> Delete
          </Button>
        </div>
      </header>

      <Tabs defaultValue="overview">
        <TabsList className="overflow-x-auto -mx-1 px-1 max-w-full whitespace-nowrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="logs">Logs ({logs.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="photos">Photos ({photos.data?.length ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {project.description ? (
            <Card>
              <CardHeader className="pb-2 space-y-1">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Description
                </p>
                <CardTitle className="font-display text-base">What's this project</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 whitespace-pre-wrap leading-relaxed">
                {project.description}
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-muted-foreground">No description yet — Edit to add one.</p>
          )}
        </TabsContent>
        <TabsContent value="logs">
          <LogList logs={logs.data ?? []} emptyMessage="No logs for this project yet." />
        </TabsContent>
        <TabsContent value="photos">
          <PhotoGrid assets={photos.data ?? []} emptyMessage="No photos for this project yet." />
        </TabsContent>
      </Tabs>

      <ProjectForm open={editing} onOpenChange={setEditing} mode="edit" project={project} />
    </div>
  );
}
